import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import StatusPill from "../components/StatusPill";
import { Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function LeaveApprovals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("submitted");
  const [reviewing, setReviewing] = useState(null);

  const load = () => api.get("/leaves", { params: { status: filter || undefined } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const isAdmin = user.role === "admin";

  const doAction = async (id, level, action, remarks) => {
    try {
      await api.post(`/leaves/${id}/${level}-review`, { action, remarks });
      toast.success(`${action}d`);
      setReviewing(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  return (
    <div>
      <PageHeader eyebrow="Approvals" title="Leave Approvals" subtitle={isAdmin ? "Manager & final HOD approval on leave." : "Review team leave requests."} />
      <div className="flex gap-3 mb-6 flex-wrap">
        {[
          { v: "submitted", l: "Awaiting Manager" },
          ...(isAdmin ? [{ v: "manager_approved", l: "Awaiting HOD" }] : []),
          { v: "admin_approved", l: "Approved" },
          { v: "rejected", l: "Rejected" },
        ].map((t) => (
          <button key={t.v} onClick={() => setFilter(t.v)} data-testid={`leave-tab-${t.v}`}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-2 border rounded-sm ${filter === t.v ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-slate-400"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <Card>
        {items.length === 0 ? <EmptyState title="No requests" /> : (
          <div className="overflow-x-auto">
            <table className="w-full tech-table">
              <thead><tr>
                <th>From</th><th>To</th><th>Engineer</th><th>Type</th><th>Reason</th><th>Status</th><th>Action</th>
              </tr></thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} data-testid={`lv-row-${l.id}`}>
                    <td className="mono">{l.from_date}</td>
                    <td className="mono">{l.to_date}</td>
                    <td>{l.engineer_name}</td>
                    <td className="capitalize">{l.leave_type}</td>
                    <td className="text-xs text-slate-400 max-w-xs truncate">{l.reason}</td>
                    <td><StatusPill status={l.status} /></td>
                    <td>
                      {l.status === "submitted" && (
                        <button onClick={() => setReviewing({ id: l.id, level: "manager", leave: l })} className="border border-white/15 hover:border-[#FF5722] text-xs px-2 py-1 mono uppercase" data-testid={`lv-review-${l.id}`}>Review</button>
                      )}
                      {l.status === "manager_approved" && isAdmin && (
                        <button onClick={() => setReviewing({ id: l.id, level: "admin", leave: l })} className="border border-white/15 hover:border-[#FF5722] text-xs px-2 py-1 mono uppercase" data-testid={`lv-final-${l.id}`}>Final</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {reviewing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#13151A] border border-white/10 w-full max-w-md">
            <div className="px-6 py-4 border-b border-white/10">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF5722]">{reviewing.level === "admin" ? "HOD Final Approval" : "Manager Review"}</div>
              <h2 className="text-lg font-semibold">Review Leave</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><div className="mono text-[10px] text-slate-500 uppercase">Engineer</div>{reviewing.leave.engineer_name}</div>
                <div><div className="mono text-[10px] text-slate-500 uppercase">Type</div><span className="capitalize">{reviewing.leave.leave_type}</span></div>
                <div><div className="mono text-[10px] text-slate-500 uppercase">From</div><span className="mono">{reviewing.leave.from_date}</span></div>
                <div><div className="mono text-[10px] text-slate-500 uppercase">To</div><span className="mono">{reviewing.leave.to_date}</span></div>
                <div className="col-span-2"><div className="mono text-[10px] text-slate-500 uppercase">Reason</div>{reviewing.leave.reason}</div>
              </div>
              <Field label="Remarks"><textarea id="rem" rows={2} className="input" data-testid="lv-remarks" /></Field>
            </div>
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
              <button onClick={() => doAction(reviewing.id, reviewing.level, "reject", document.getElementById("rem").value)} className="border border-red-500/40 text-red-400 hover:bg-red-500/10 px-4 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="lv-reject">
                <X className="w-4 h-4" /> Reject
              </button>
              <button onClick={() => doAction(reviewing.id, reviewing.level, "approve", document.getElementById("rem").value)} className="bg-[#FF5722] hover:bg-[#F4511E] text-white px-5 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="lv-approve">
                <Check className="w-4 h-4" /> Approve
              </button>
            </div>
            <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
          </div>
        </div>
      )}
    </div>
  );
}
